/**
 * GENERIC AI ASSISTANT PANEL.
 *
 * Entry input → found-subject card → one question at a time (chips and/or
 * free text) → draft review → "apply to form". All copy, all requests and
 * the form mapping come from the `playbook` prop; this file has no module
 * knowledge and must stay that way — a new module adds a playbook, not a
 * branch here.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowRight, Check, CheckCircle2, RotateCcw, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import type { AiAssistantPlaybook, AssistantQuestion } from './types';

type Phase = 'entry' | 'interview' | 'drafting' | 'draft' | 'applied';

interface TranscriptLine { id: number; who: 'assistant' | 'user'; text: string }

const errText = (e: unknown, fallback: string) => {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string } | undefined;
  const m = err?.response?.data?.message;
  if (Array.isArray(m)) return m.join(', ');
  return m ?? err?.message ?? fallback;
};
const alreadyToasted = (e: unknown) => !!(e as { response?: unknown } | undefined)?.response;

const Spinner = ({ className }: { className?: string }) => (
  <span aria-hidden className={clsx('inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent', className)} />
);

export function AiAssistantPanel<TSubject, TDraft, TForm>({ playbook, form, onApplied, onClose, className }: {
  playbook: AiAssistantPlaybook<TSubject, TDraft, TForm>;
  form: TForm;
  onApplied?: (ctx: { sessionId: string; subject: TSubject; draft: TDraft }) => void;
  onClose?: () => void;
  className?: string;
}) {
  const [phase, setPhase] = useState<Phase>('entry');
  const [entry, setEntry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [subject, setSubject] = useState<TSubject | null>(null);
  const [question, setQuestion] = useState<AssistantQuestion | null>(null);
  const [freeText, setFreeText] = useState('');
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [draft, setDraft] = useState<TDraft | null>(null);
  const lineId = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const say = useCallback((who: TranscriptLine['who'], text: string) => {
    setTranscript((t) => [...t, { id: ++lineId.current, who, text }]);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [transcript, question, phase]);

  const reset = () => {
    setPhase('entry'); setEntry(''); setBusy(false); setError(null); setSessionId(null); setSubject(null);
    setQuestion(null); setFreeText(''); setTranscript([]); setDraft(null);
  };

  const runDraft = useCallback(async (sid: string, subj: TSubject) => {
    setPhase('drafting'); setBusy(true); setError(null);
    try {
      const d = await playbook.draft(sid, subj);
      setDraft(d);
      setPhase('draft');
    } catch (e) {
      setError(errText(e, 'Could not build the draft.'));
      setPhase('interview');
    } finally { setBusy(false); }
  }, [playbook]);

  const start = async () => {
    if (!entry.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await playbook.start(entry.trim());
      setSessionId(r.sessionId); setSubject(r.subject);
      say('user', entry.trim());
      if (r.question) { setQuestion(r.question); setPhase('interview'); }
      else await runDraft(r.sessionId, r.subject);
    } catch (e) {
      setError(errText(e, 'Could not start.'));
    } finally { setBusy(false); }
  };

  const answer = async (value: string, shown?: string) => {
    if (!sessionId || !subject || !value.trim()) return;
    setBusy(true); setError(null);
    say('user', shown ?? value);
    setFreeText('');
    try {
      const r = await playbook.answer(sessionId, value.trim());
      if (r.acknowledged) say('assistant', r.acknowledged);
      if (r.done || !r.question) { setQuestion(null); await runDraft(sessionId, subject); }
      else setQuestion(r.question);
    } catch (e) {
      setError(errText(e, 'That did not go through.'));
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!draft || !sessionId || !subject) return;
    try {
      playbook.applyDraft(draft, form, { sessionId, subject });
      setPhase('applied');
      onApplied?.({ sessionId, subject, draft });
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(errText(e, 'Could not apply the draft.'));
    }
  };

  // ── progress rail ──
  const stepIdx = phase === 'entry' ? 0
    : phase === 'interview' && question ? 1 + question.progress.index
    : playbook.steps.length - 1;

  const card = subject ? playbook.describeSubject(subject) : null;
  const rows = draft ? playbook.summarizeDraft(draft) : [];
  const warnings = draft && playbook.draftWarnings ? playbook.draftWarnings(draft) : [];

  return (
    <div className={clsx('flex flex-col overflow-hidden rounded-2xl border border-primary/30 bg-surface shadow-soft', className)}>
      {/* header */}
      <div className="flex items-center gap-3 border-b border-border bg-primary-soft/40 px-4 py-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary"><Sparkles size={15} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-text-primary">{playbook.title}</div>
          <div className="truncate text-2xs text-text-muted">{playbook.description}</div>
        </div>
        {phase !== 'entry' && (
          <button type="button" onClick={reset} className="flex h-7 items-center gap-1 rounded-lg px-2 text-2xs text-text-muted hover:bg-surface-alt hover:text-text-primary" aria-label="Start over">
            <RotateCcw size={12} /> Start over
          </button>
        )}
        {onClose && (
          <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary" aria-label="Close assistant">
            <X size={14} />
          </button>
        )}
      </div>

      {/* rail */}
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-1 border-b border-border px-4 py-2 text-2xs">
        {playbook.steps.map((label, i) => {
          const done = i < stepIdx || phase === 'applied';
          const active = i === stepIdx && phase !== 'applied';
          return (
            <li key={label} className="flex items-center gap-1">
              <span className={clsx('flex h-4 w-4 items-center justify-center rounded-full border text-[9px] font-semibold',
                done ? 'border-success bg-success text-white' : active ? 'border-primary text-primary' : 'border-border text-text-muted')}>
                {done ? <Check size={9} /> : i + 1}
              </span>
              <span className={clsx(active ? 'font-medium text-text-primary' : 'text-text-muted')}>{label}</span>
              {i < playbook.steps.length - 1 && <span className="mx-0.5 text-border-strong">›</span>}
            </li>
          );
        })}
      </ol>

      <div ref={scrollRef} className="max-h-[520px] overflow-y-auto px-4 py-4">
        {/* entry */}
        {phase === 'entry' && (
          <div className="space-y-3">
            <div>
              <span className="block text-xs font-medium text-text-secondary">{playbook.entry.label}</span>
              <div className="mt-1.5 flex gap-2">
                <Input value={entry} placeholder={playbook.entry.placeholder} onChange={(e) => setEntry(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); start(); } }} disabled={busy} />
                <Button onClick={start} loading={busy} disabled={busy || !entry.trim()} iconRight={<ArrowRight size={14} />}>{playbook.entry.buttonLabel}</Button>
              </div>
              {playbook.entry.hint && <p className="mt-1.5 text-2xs text-text-muted">{playbook.entry.hint}</p>}
            </div>
          </div>
        )}

        {/* subject card */}
        {card && phase !== 'entry' && (
          <div className="mb-4 flex gap-3 rounded-xl border border-border bg-surface-alt/50 p-3">
            {card.imageUrl
              // no-referrer: Google's play-lh CDN answers an opaque response when a Referer is present and Chrome
              // blocks it (ERR_BLOCKED_BY_ORB) — the same URL loads fine without one.
              ? <img src={card.imageUrl} alt="" referrerPolicy="no-referrer" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
              : <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-surface-alt text-text-muted"><Sparkles size={18} /></span>}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{card.title}</div>
              {card.subtitle && <div className="truncate text-2xs text-text-muted">{card.subtitle}</div>}
              {card.badges && card.badges.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {card.badges.map((b) => <span key={b} className="rounded-full bg-surface px-2 py-0.5 text-[10px] text-text-secondary">{b}</span>)}
                </div>
              )}
              {card.description && <p className="mt-1.5 line-clamp-2 text-2xs leading-relaxed text-text-muted">{card.description}</p>}
            </div>
          </div>
        )}

        {/* transcript */}
        {transcript.length > 0 && (
          <div className="mb-3 space-y-1.5">
            {transcript.map((l) => (
              <div key={l.id} className={clsx('flex', l.who === 'user' ? 'justify-end' : 'justify-start')}>
                <span className={clsx('max-w-[85%] rounded-2xl px-3 py-1.5 text-xs leading-relaxed',
                  l.who === 'user' ? 'bg-primary-soft text-text-primary' : 'bg-surface-alt text-text-secondary')}>
                  {l.text}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* question */}
        {phase === 'interview' && question && (
          <div className="space-y-3">
            <div>
              <div className="text-sm font-medium text-text-primary">{question.prompt}</div>
              {question.hint && <p className="mt-1 text-2xs text-text-muted">{question.hint}</p>}
            </div>
            {question.options && question.options.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {question.options.map((o) => (
                  <button key={o.value} type="button" disabled={busy} title={o.description}
                    onClick={() => answer(o.value, o.label)}
                    className={clsx('rounded-full border px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-60',
                      o.recommended ? 'border-primary bg-primary-soft/50 text-primary hover:bg-primary-soft' : 'border-border text-text-primary hover:border-primary hover:bg-surface-alt')}>
                    {o.label}{o.recommended && <span className="ml-1.5 text-[10px] font-semibold uppercase">recommended</span>}
                  </button>
                ))}
              </div>
            )}
            {question.allowFreeText && (
              <div className="flex gap-2">
                <Input value={freeText} placeholder={question.placeholder ?? 'Type your answer…'} disabled={busy}
                  onChange={(e) => setFreeText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); answer(freeText); } }} />
                <Button variant="secondary" onClick={() => answer(freeText)} disabled={busy || !freeText.trim()} loading={busy} aria-label="Send answer">
                  <ArrowRight size={14} />
                </Button>
              </div>
            )}
          </div>
        )}

        {phase === 'drafting' && (
          <div className="flex items-center gap-2 py-4 text-xs text-text-muted"><Spinner className="text-primary" /> Building your draft…</div>
        )}

        {/* draft review */}
        {(phase === 'draft' || phase === 'applied') && draft && (
          <div className="space-y-3">
            <dl className="divide-y divide-border rounded-xl border border-border">
              {rows.map((r) => (
                <div key={r.key} className="px-3 py-2">
                  <div className="flex gap-3">
                    <dt className="w-28 shrink-0 text-xs text-text-muted">{r.label}</dt>
                    <dd className="min-w-0 flex-1 break-words text-[13px] text-text-primary">{r.value}</dd>
                  </div>
                  {r.rationale && <p className="mt-0.5 pl-[7.75rem] text-2xs leading-relaxed text-text-muted">{r.rationale}</p>}
                </div>
              ))}
            </dl>
            {warnings.length > 0 && (
              <ul className="space-y-1 rounded-xl border border-warning/30 bg-warning-soft px-3 py-2">
                {warnings.map((w) => <li key={w} className="text-2xs text-warning">{w}</li>)}
              </ul>
            )}
            {phase === 'applied'
              ? <div className="flex items-center gap-2 text-xs text-success"><CheckCircle2 size={14} /> Applied — every field is still yours to edit. Nothing has been published.</div>
              : <div className="flex justify-end"><Button onClick={apply} iconLeft={<Check size={14} />}>{playbook.applyLabel}</Button></div>}
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-xl border border-danger/30 bg-danger-soft p-3">
            <p className="break-words text-xs text-danger">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
